import React, {useMemo, useState} from 'react'

import {
  getUpdatedDateRangeByFrom,
  getUpdatedDateRangeByTo,
} from '../../../commons/src/libs/date-range'
import {
  checkDateInputSupported,
  getDateXDaysBefore,
  getTodaysDate,
} from '../../../commons/src/services/date'

import {DateRange, DateRangeConfig} from '../../../commons/src/types'

import ActionBar from '../../../commons/src/components/action-bar/ActionBar'
import DateInputField from '../../../commons/src/components/date-input-field/DateInputField'
import Paragraph from '../../../commons/src/components/paragraph'
import Stack from '../../../commons/src/components/stack'

const isDateInputSupported = checkDateInputSupported()

type Props = {
  inputLabel: string
  dateRange: DateRange
  dateRangeConfig: DateRangeConfig
  disabled?: boolean
  onChange: (dateRange: DateRange) => void
}
const DateRangePartial = ({
  inputLabel,
  dateRange,
  dateRangeConfig,
  disabled = false,
  onChange,
}: Props) => {
  const [error, setError] = useState(false)

  const memoizedMinDate = useMemo(
    () =>
      dateRangeConfig.earliestFromTodayDays
        ? getDateXDaysBefore(getTodaysDate(), dateRangeConfig.earliestFromTodayDays)
        : dateRangeConfig.defaultMinDate,
    [dateRangeConfig]
  )

  const handleRangeChange = (newDateRange: DateRange | null) => {
    if (newDateRange) {
      onChange(newDateRange)
    } else {
      setError(true)
    }
  }

  if (!isDateInputSupported) {
    return (
      <Paragraph warning>
        Your current browser version does not support date pickers. Please update your browser and
        try again.
      </Paragraph>
    )
  }

  return (
    <Stack>
      <ActionBar noWrap>
        <DateInputField
          value={dateRange.from}
          onChange={newFromValue =>
            handleRangeChange(
              getUpdatedDateRangeByFrom(dateRangeConfig.maxDaysRange, dateRange.to, newFromValue)
            )
          }
          label={`${inputLabel} from`}
          min={memoizedMinDate}
          max={getTodaysDate()}
          error={error}
          disabled={disabled}
        />
        <DateInputField
          value={dateRange.to}
          onChange={newToValue =>
            handleRangeChange(
              getUpdatedDateRangeByTo(dateRangeConfig.maxDaysRange, dateRange.from, newToValue)
            )
          }
          label={`${inputLabel} to`}
          min={memoizedMinDate}
          max={getTodaysDate()}
          error={error}
          disabled={disabled}
        />
      </ActionBar>
    </Stack>
  )
}

export default DateRangePartial
